import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import { formatDate, formatRelativeTime } from 'features/agent-analytics/components/runCells';
import IssuesFilter from 'features/issues/components/IssuesFilter';
import Sparkline from 'features/issues/components/Sparkline';
import { StatusDot, CategoryTag, STATUS, CATEGORY } from 'features/issues/components/IssueTags';
import IssueDrawer from 'features/issues/components/IssueDrawer';
import TrackIssuesModal from 'features/issues/components/TrackIssuesModal';
import MergeTarget from 'features/issues/components/MergeTarget';
import BulkBar from 'shared/components/tables/BulkBar';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';
import { listIssues, setIssueStatus, mergeIssue, errorText } from 'features/issues/api/issuesApi';
import {
  activitySearchWrapperClass,
  activitySearchInputClass,
  activityToolbarButtonClass,
  tableHeaderCellClass,
  tableDataCellClass,
  tableRowClass,
  tableHeaderRowClass,
} from 'features/activity/styles/dataTableStyles';
import TableFooter from 'shared/components/tables/TableFooter';

const PAGE_SIZE = 25;
const STATUS_OPTIONS = ['open', 'regression', 'closed'].map((v) => ({ value: v, label: STATUS[v].label, dot: STATUS[v].dot }));
const CATEGORY_OPTIONS = [...Object.entries(CATEGORY).map(([value, c]) => ({ value, label: c.label })), { value: 'none', label: 'Not set' }];
const DAYS = 30;

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-[color:var(--text-muted)]">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

const muted = <span className="text-[color:var(--text-muted)]">—</span>;

/** The last DAYS days as keys, oldest first, for the per-issue trend. */
function lastDays(n = DAYS, now = new Date()) {
  const end = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Array.from({ length: n }, (_, i) => new Date(end - (n - 1 - i) * 86400000).toISOString().slice(0, 10));
}

/**
 * Issues v6 list (TRO-481): what is wrong, what kind of fix it needs, and how
 * much evidence is behind it. The dot is the status; the agent is a filter,
 * not a column. A row opens the issue.
 */
export default function IssuesList({ teamId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [statusSel, setStatusSel] = useState(['open', 'regression']);
  const [categorySel, setCategorySel] = useState(CATEGORY_OPTIONS.map((o) => o.value));
  const [agentSel, setAgentSel] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(-1);
  // ?issue=<id> opens that issue — the link a run's Signals tab follows.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = searchParams.get('issue');
  const closeLinked = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('issue');
    setSearchParams(next, { replace: true });
  };
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);
  const [tracking, setTracking] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Untouched, the agent filter is "every agent" and sends nothing — so the
  // agent list arriving with the first page does not fetch the page again.
  const emptyFilter = statusSel.length === 0 || categorySel.length === 0 || (agentSel != null && agentSel.length === 0);
  const query = useCallback((offset) => ({
    status: statusSel,
    categories: categorySel.length === CATEGORY_OPTIONS.length ? [] : categorySel,
    agents: agentSel == null ? [] : agentSel,
    search,
    limit: PAGE_SIZE,
    offset,
  }), [statusSel, categorySel, agentSel, search]);

  const reload = useCallback(() => {
    if (!teamId) return;
    if (emptyFilter) { setRows([]); setTotal(0); return; }
    setLoading(true);
    setError(null);
    listIssues(teamId, query(0))
      .then((r) => {
        setRows(r.issues);
        setTotal(r.total);
        setAgents((prev) => (prev.length === r.agents.length && prev.every((a, i) => a === r.agents[i]) ? prev : r.agents));
      })
      .catch((e) => setError(errorText(e, 'Failed to load issues')))
      .finally(() => setLoading(false));
  }, [teamId, emptyFilter, query]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { setSelectedIdx(-1); }, [statusSel, categorySel, agentSel, search]);

  const loadMore = () => {
    if (loadingMore || rows.length >= total) return;
    setLoadingMore(true);
    listIssues(teamId, query(rows.length))
      .then((r) => setRows((prev) => {
        const seen = new Set(prev.map((x) => String(x.id)));
        return [...prev, ...r.issues.filter((x) => !seen.has(String(x.id)))];
      }))
      .catch((e) => setError(errorText(e, 'Failed to load issues')))
      .finally(() => setLoadingMore(false));
  };

  const days = useMemo(() => lastDays(), []);
  const selected = selectedIdx >= 0 ? rows[selectedIdx] : null;

  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const key = (id) => String(id);
  // A reload or a filter can drop rows the selection still holds.
  useEffect(() => { setPicked((cur) => { const ids = new Set(rows.map((r) => key(r.id))); return new Set([...cur].filter((id) => ids.has(id))); }); }, [rows]);
  const chosen = rows.filter((r) => picked.has(key(r.id)));
  const allPicked = rows.length > 0 && rows.every((r) => picked.has(key(r.id)));
  const toggleOne = (id) => setPicked((cur) => { const n = new Set(cur); if (n.has(key(id))) n.delete(key(id)); else n.add(key(id)); return n; });
  // Merge targets come from the loaded rows, never a picked one.
  const mergeOptions = useMemo(() => rows.filter((r) => !picked.has(key(r.id)) && r.status !== 'closed' && r.status !== 'merged'), [rows, picked]);

  /** One call per issue; the first failure is said, what worked is kept. */
  const each = async (targets, fn) => {
    setBusy(true);
    const res = await Promise.allSettled(targets.map(fn));
    const failed = res.filter((x) => x.status === 'rejected');
    setBulkError(failed.length ? errorText(failed[0].reason, 'Something failed') : '');
    setPicked(new Set());
    reload();
    setBusy(false);
  };
  const closeChosen = () => each(chosen.filter((r) => r.status !== 'closed'), (r) => setIssueStatus(teamId, r.id, 'closed'));
  const mergeChosen = (target) => each(chosen, (r) => mergeIssue(teamId, r.id, target.id));

  const onChanged = useCallback((updated) => {
    if (!updated) return;
    setRows((prev) => prev.map((r) => (String(r.id) === String(updated.id) ? { ...r, ...updated } : r)));
  }, []);

  return (
    <>
      <div className="flex flex-col gap-2 bg-surface px-3 sm:px-4 pb-2 pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex h-8 items-center overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px]">
            <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">{loading ? '…' : total.toLocaleString()}</span>
            <span className="ml-1.5 text-[color:var(--text-muted)]">issue{total === 1 ? '' : 's'}</span>
          </div>
          <div className={`${activitySearchWrapperClass} min-w-[220px] flex-1 sm:max-w-sm`}>
            <SearchIcon />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search title or root cause…"
              className={activitySearchInputClass}
            />
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {canWrite && (
              <button
                type="button"
                onClick={() => setTracking(true)}
                className={activityToolbarButtonClass}
              >
                Track issues
              </button>
            )}
            <IssuesFilter
              sections={[
                { id: 'status', label: 'Status', options: STATUS_OPTIONS, selected: statusSel, onChange: setStatusSel },
                { id: 'category', label: 'Category', options: CATEGORY_OPTIONS, selected: categorySel, onChange: setCategorySel },
                {
                  id: 'agent',
                  label: 'Agent',
                  options: agents.map((a) => ({ value: a, label: a })),
                  selected: agentSel || agents,
                  onChange: (next) => setAgentSel(next.length === agents.length ? null : next),
                },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pb-8 pt-3">
        {bulkError ? (
          <div className="rounded-lg border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-3 text-[12px] text-[color:var(--feedback-error)]">{bulkError}</div>
        ) : null}
        {error ? (
          <div className="rounded-lg border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-3 text-[12px] text-[color:var(--feedback-error)]">{error}</div>
        ) : null}

        {loading && rows.length === 0 ? (
          <PageLoader label="Loading issues" />
        ) : rows.length === 0 ? (
          <div className="flex h-48 items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface text-[12px] text-[color:var(--text-muted)]">
            {emptyFilter ? 'No filters selected.' : search ? 'No issues match your search.' : 'No issues.'}
          </div>
        ) : (
          <>
            <div className="shrink-0 overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-sm">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] table-fixed text-left" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <colgroup>
                    <col style={{ width: '32px' }} />
                    <col style={{ width: '54%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '11%' }} />
                  </colgroup>
                  <thead>
                    <tr className={tableHeaderRowClass}>
                      <th scope="col" className={tableHeaderCellClass}>
                        <input type="checkbox" checked={allPicked} aria-label={allPicked ? 'Clear the selection' : 'Select all'}
                          onChange={() => setPicked(allPicked ? new Set() : new Set(rows.map((r) => key(r.id))))} />
                      </th>
                      <th className={tableHeaderCellClass}>Issue</th>
                      <th className={tableHeaderCellClass}>Category</th>
                      <th className={tableHeaderCellClass}>Evidence</th>
                      <th className={tableHeaderCellClass}>Trend</th>
                      <th className={tableHeaderCellClass}>Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => {
                      const byDay = new Map((r.daily || []).map((d) => [d.day, d.count]));
                      return (
                        <tr
                          key={r.id}
                          className={`${tableRowClass} ${selected && String(selected.id) === String(r.id) ? 'bg-surface-hover' : ''}`}
                          onClick={() => { if (linkedId) closeLinked(); setSelectedIdx(idx); }}
                        >
                          <td className={tableDataCellClass} onClick={(e) => e.stopPropagation()}>
                            {canWrite && <input type="checkbox" checked={picked.has(key(r.id))} aria-label={`Select ${r.title}`} onChange={() => toggleOne(r.id)} />}
                          </td>
                          <td className={tableDataCellClass}>
                            <span className="flex min-w-0 items-center gap-2">
                              <StatusDot status={r.status} />
                              <span className="truncate text-[12px] font-medium text-[color:var(--text-primary)]" title={r.title}>{r.title}</span>
                            </span>
                          </td>
                          <td className={tableDataCellClass}>{r.category ? <CategoryTag category={r.category} /> : muted}</td>
                          <td className={tableDataCellClass}>
                            <span className="tabular-nums text-[12px] text-[color:var(--text-primary)]">{(r.evidence_count || 0).toLocaleString()}</span>
                          </td>
                          <td className={tableDataCellClass}>
                            <Sparkline counts={days.map((d) => ({ bucket: d, count: byDay.get(d) || 0 }))} width={72} height={18} compact />
                          </td>
                          <td className={tableDataCellClass}>
                            <span className="tabular-nums text-[11px] text-[color:var(--text-muted)]" title={formatDate(r.last_seen_at)}>{formatRelativeTime(r.last_seen_at)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <TableFooter
              shown={rows.length}
              total={total}
              hasMore={rows.length < total}
              loading={loadingMore}
              onLoadMore={loadMore}
            />
          </>
        )}
      </div>
        <BulkBar count={canWrite ? picked.size : 0} label="Selected issues" onClear={() => setPicked(new Set())} actions={[
          { label: 'Mark closed', disabled: busy || !chosen.some((r) => r.status !== 'closed'), onClick: closeChosen },
        ]}>
          <MergeTarget options={mergeOptions} disabled={busy} onPick={mergeChosen} />
        </BulkBar>

      {tracking ? <TrackIssuesModal teamId={teamId} onClose={() => setTracking(false)} /> : null}

      {selected ? (
        <IssueDrawer
          teamId={teamId}
          issueId={selected.id}
          initial={selected}
          onClose={() => setSelectedIdx(-1)}
          onPrev={() => setSelectedIdx((i) => Math.max(0, i - 1))}
          onNext={() => setSelectedIdx((i) => Math.min(rows.length - 1, i + 1))}
          hasPrev={selectedIdx > 0}
          hasNext={selectedIdx >= 0 && selectedIdx < rows.length - 1}
          onChanged={(u) => { onChanged(u); if (u?.status === 'merged') reload(); }}
          openIssues={rows.filter((r) => r.status !== 'closed')}
        />
      ) : linkedId ? (
        <IssueDrawer
          teamId={teamId}
          issueId={linkedId}
          onClose={closeLinked}
          onChanged={(u) => { onChanged(u); if (u?.status === 'merged') reload(); }}
          openIssues={rows.filter((r) => r.status !== 'closed')}
        />
      ) : null}
    </>
  );
}
